import * as _ from "lodash";
import moment from "moment";

import { Nullable, readableHash } from "@ctra/utils";

import {
  ChartEntity,
  ChartFilterDefinition,
  ChartFilters,
  ChartTimePeriod,
  ChartDataSource,
  ChartData,
  UnitSystemParam,
  ChartType,
  LineSource
} from "./typings";

import { Normalizer } from "./normalizer";
import { FarmEntity } from "../farms";

/**
 * Supported duration values which are valid as time unit
 * @type {readonly [string, string]}
 */
const durations = ["PT1H", "P1D"] as const;

/**
 * Supported duration values which are valid as time period
 * @type {readonly [string, string, string, string, string, string, string, string, string, string]}
 */
export const supportedDurations = [
  "P1W",
  "P2W",
  "P3W",
  "P1M",
  "P2M",
  "P3M",
  "P6M",
  "P9M",
  "P1Y",
  "P5Y"
] as const;

/**
 * @todo type this for better support
 */
export type ISODuration = typeof durations[number] | typeof supportedDurations[number];

/**
 * Make a hash from the given chart id and filters.
 * @param {ChartEntity["id"]} chartID
 * @param {ChartTimePeriod} timePeriod
 * @param {ISODuration} bucketSize
 * @param {Array<FarmEntity["id"]>} farmIDs
 * @param {UnitSystemParam} unitSystem
 * @param {ChartFilters} filters
 * @returns {string}
 * @example
 * ```ts
 * makeChartHash(
 *   "1234-asdf-2345-sdfg",
 *   { startDate: "2022-02-01T00:00:00.000Z", endDate: "2022-03-03T00:00:00.000Z" },
 *   { farmIDs: [33, 38] }
 * );
 * // "a23dd9603622592f2ee01de3d6407d49b0e328c7"
 * ```
 */
export const makeChartHash = (
  chartID: ChartEntity["id"],
  timePeriod: ChartTimePeriod,
  bucketSize: Nullable<ISODuration>,
  farmIDs: Array<FarmEntity["id"]>,
  unitSystem: UnitSystemParam,
  filters?: ChartFilters
): string =>
  readableHash({
    chartID,
    timePeriod,
    bucketSize,
    farmIDs,
    unitSystem,
    filters
  });

/**
 * Make a hash for chart overlays. This should be shared alongside the cards
 * and the hash will point to a list of data descriptors/chart hashes in the store.
 * @returns {string}
 */
export const makeChartOverlayHash = (): string => {
  return readableHash({
    timeStamp: new Date().getTime()
  });
};

/**
 * Gets default filters
 * Filters out unitSystem filters and assigns filters to empty array values
 * @param {Array<ChartFilterDefinition>} filterDefinitions
 * @return {ChartFilters}
 *
 * @example
 * ```ts
 * getDefaultFilters([{ name: dimGroupKey, isMultiSelectable: true }, { name: unitSystem, isMultiSelectable: true }]);
 * // {dimGroupKey: []}
 * ```
 */
export const getDefaultFilters = (filterDefinitions: Array<ChartFilterDefinition>) => {
  return _.reduce<ChartFilterDefinition, ChartFilters>(
    filterDefinitions,
    (res, filter) => {
      const key = filter.name;

      // filters are considered only backend aggregated filters
      const { isBackendAggregated } = filter;

      if (key && isBackendAggregated) {
        /**
         * Naive assumption that every filter is an array.
         * @todo keep an eye on non-array filters
         */
        res[key] = [];
      }

      return res;
    },
    {}
  );
};

/**
 * Process the incoming chart data
 * @param {ChartDataSource} input
 * @param {Nullable<ISODuration>} zeroRenderingInterval
 * @param {Nullable<ChartTimePeriod>} timePeriod
 * @returns {ChartData}
 */
export const normalizeChartData = (
  input: ChartDataSource,
  {
    zeroRenderingInterval,
    timePeriod
  }: { zeroRenderingInterval: Nullable<ISODuration>; timePeriod: Nullable<ChartTimePeriod> }
): ChartData => {
  const normalizer = Normalizer.create(input);
  return normalizer.format({ zeroRenderingInterval, timePeriod });
};

/**
 * Tell whether the chart supports bucket size
 * @param {ChartType} view
 * @returns {boolean}
 */
export const hasBucketSizeSupport = (view?: ChartType): boolean =>
  !!view && [ChartType.basicLine, ChartType.table].includes(view);

/**
 * Normalizes date strings to a consistent format for comparison
 * Converts formats like "2025-04-17T00:00:00.000Z" to "2025-04-30T00:00:00+00:00"
 * @param {string} dateString
 * @returns {string}
 */
function normalizeDateString(dateString: string): string {
  return moment(dateString).utc().format("YYYY-MM-DDTHH:mm:ss") + "+00:00";
}

/**
 * Generates an array of ISO date strings from start to end date with given interval
 * @param {string} startDate
 * @param {string} endDate
 * @param {string} isoDuration
 * @returns {string[]}
 */
function generateDateRange(startDate: string, endDate: string, isoDuration: string): string[] {
  const dates: string[] = [];
  const start = moment(startDate);
  const end = moment(endDate);

  let current = moment(start);

  while (current.isSameOrBefore(end)) {
    dates.push(normalizeDateString(current.toISOString()));
    current = current.add(moment.duration(isoDuration));
  }

  return dates;
}

/**
 * Fills gaps in time series data with zero values
 * @param {LineSource["data"]} data
 * @param {string} isoDuration
 * @param {string} startDate
 * @param {string} endDate
 * @returns {LineSource["data"]}
 */
export const fillTimeSeriesGaps = (
  data: LineSource["data"],
  isoDuration: string,
  startDate: string,
  endDate: string
): LineSource["data"] => {
  const expectedDates = generateDateRange(startDate, endDate, isoDuration);

  return _.mapValues(data, (seriesData) => {
    const existingPointsMap = _.keyBy(seriesData.points, "x");

    const filledPoints = expectedDates.map((date) => {
      const existingPoint = existingPointsMap[date];

      return (
        existingPoint || {
          x: date,
          y: 0,
          context: {
            _hideFromTooltip: true
          }
        }
      );
    });

    return {
      ...seriesData,
      points: filledPoints
    };
  });
};
